import React, { useMemo, useState } from 'react';
import { MarketInsightCard } from './MarketInsightCard';
import { MarketFilters } from './MarketFilters';
import { MarketDataTable } from './MarketDataTable';
import {
  buildLiveMarketRows, buildLiveMarketHighlights, buildUnderOwnedOpportunities,
} from '../../utils/marketAnalytics';
import {
  formatPct, formatRating, formatSharePrice, formatNumber,
  getRatingBandTone, getValueBucketTone, DASH,
} from '../../utils/marketFormatters';

function RatingPill({ row }) {
  return <span className={`mk-pill mk-pill--${getRatingBandTone(row.ratingBand)}`}>{formatRating(row.displayRating)}</span>;
}
function ValuePill({ row }) {
  return <span className={`mk-pill mk-pill--${getValueBucketTone(row.valueLabel)}`}>{row.valueLabel}</span>;
}

const MAIN_COLUMNS = [
  { key: 'runnerName', label: 'Runner', sortable: true },
  { key: 'slateName', label: 'Showdown', sortable: true },
  { key: 'raceNumber', label: 'Race', align: 'center', render: r => r.raceNumber ? `R${r.raceNumber}` : DASH },
  { key: 'displayRating', label: 'Rating', align: 'center', sortable: true, render: r => <RatingPill row={r} /> },
  { key: 'valueLabel', label: 'Value', align: 'center', render: r => <ValuePill row={r} /> },
  { key: 'sharePrice', label: 'Share Price', align: 'right', sortable: true, render: r => formatSharePrice(r.sharePrice) },
  { key: 'ownershipUserPct', label: 'Owned By %', align: 'right', sortable: true, render: r => formatPct(r.ownershipUserPct) },
  { key: 'ownershipUserCount', label: 'Owners', align: 'right', sortable: true, render: r => formatNumber(r.ownershipUserCount) },
  { key: 'totalEquityPurchased', label: 'Total Equity', align: 'right', sortable: true, render: r => formatPct((r.totalEquityPurchased || 0) / 100, 0) },
  { key: 'jockey', label: 'Jockey' },
  { key: 'trainer', label: 'Trainer' },
];

const UNDER_OWNED_COLUMNS = [
  { key: 'runnerName', label: 'Runner', sortable: true },
  { key: 'displayRating', label: 'Rating', align: 'center', sortable: true, render: r => <RatingPill row={r} /> },
  { key: 'valueLabel', label: 'Value', align: 'center', render: r => <ValuePill row={r} /> },
  { key: 'ownershipUserPct', label: 'Owned By %', align: 'right', sortable: true, render: r => formatPct(r.ownershipUserPct) },
  { key: 'sharePrice', label: 'Share Price', align: 'right', sortable: true, render: r => formatSharePrice(r.sharePrice) },
  { key: 'race', label: 'Race / Showdown', render: r => `${r.raceNumber ? `R${r.raceNumber} · ` : ''}${r.slateName}` },
];

export const MarketLiveTab = ({ rows, slates, loading }) => {
  const [filters, setFilters] = useState({ slateId: null, ratingBand: null, valueLabel: null, sortBy: 'rating' });

  const showdownOptions = useMemo(
    () => (slates || []).map(s => ({ id: s.id, label: s.gameName || s.title || s.id })),
    [slates]
  );

  const highlights = useMemo(() => buildLiveMarketHighlights(rows), [rows]);
  const mainRows = useMemo(() => buildLiveMarketRows(rows, filters), [rows, filters]);
  const underOwned = useMemo(() => buildUnderOwnedOpportunities(rows), [rows]);

  return (
    <div className="mk-tab-content">
      <div className="mk-insight-grid">
        <MarketInsightCard
          icon="⭐" label="Highest Rated Active Runner" accent="gold"
          value={highlights.highestRated?.runnerName}
          subtitle={highlights.highestRated ? `Rating ${formatRating(highlights.highestRated.displayRating)} · ${highlights.highestRated.slateName}` : null}
        />
        <MarketInsightCard
          icon="💎" label="Best Value Active Runner" accent="green"
          value={highlights.bestValue?.runnerName}
          subtitle={highlights.bestValue ? `${highlights.bestValue.valueLabel} · ${highlights.bestValue.slateName}` : null}
        />
        <MarketInsightCard
          icon="👥" label="Most Owned Active Runner" accent="blue"
          value={highlights.mostOwned?.runnerName}
          subtitle={highlights.mostOwned ? `${formatPct(highlights.mostOwned.ownershipUserPct)} owned · ${highlights.mostOwned.slateName}` : null}
        />
        <MarketInsightCard
          icon="🕵️" label="Lowest-Owned Elite Runner" accent="purple"
          value={highlights.lowestOwnedElite?.runnerName}
          subtitle={highlights.lowestOwnedElite ? `Rating ${formatRating(highlights.lowestOwnedElite.displayRating)} · ${formatPct(highlights.lowestOwnedElite.ownershipUserPct)} owned` : null}
        />
      </div>

      <MarketFilters showdownOptions={showdownOptions} filters={filters} onChange={setFilters} />

      <section className="mk-section">
        <MarketDataTable
          columns={MAIN_COLUMNS}
          rows={mainRows}
          getRowKey={r => `${r.slateId}-${r.raceId}-${r.runnerId}`}
          loading={loading}
          emptyIcon="⚡"
          emptyTitle="No active or upcoming runner markets available right now"
          emptyMessage="Check back once the next showdown opens for entries."
        />
      </section>

      <section className="mk-section">
        <div className="mk-section-header">
          <h2 className="mk-section-title">Under-Owned Opportunities</h2>
          <p className="mk-section-subtitle">Strong runners the crowd hasn't caught onto yet.</p>
        </div>
        <MarketDataTable
          columns={UNDER_OWNED_COLUMNS}
          rows={underOwned}
          getRowKey={r => `${r.slateId}-${r.raceId}-${r.runnerId}`}
          loading={loading}
          emptyIcon="🔍"
          emptyTitle="No under-owned opportunities right now"
          emptyMessage="Every strong runner is already being backed by the crowd."
        />
      </section>

      <p className="mk-disclaimer">
        Market insights are based on game data and virtual outcomes. They are not betting advice.
      </p>
    </div>
  );
};

export default MarketLiveTab;
